import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logRealtimeStatus } from '@/utils/realtimeStatusLog';

export interface QueuedReminder {
  id: string;
  ticket_id: string;
  reminder_type: string;
  channel: string;
  send_at: string;
}

export function useShopReminderCounts(shopId: string | undefined | null) {
  const [remindersByTicket, setRemindersByTicket] = useState<Map<string, QueuedReminder[]>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const isUnmountingRef = useRef(false);

  useEffect(() => {
    if (!shopId) {
      setRemindersByTicket(new Map());
      setIsLoading(false);
      return;
    }

    isUnmountingRef.current = false;

    const fetchReminders = async () => {
      try {
        const { data, error } = await supabase
          .from('reminder_queue')
          .select('id, ticket_id, reminder_type, channel, send_at')
          .eq('shop_id', shopId)
          .eq('status', 'queued')
          .order('send_at', { ascending: true });

        if (error) throw error;
        if (isUnmountingRef.current) return;

        const grouped = new Map<string, QueuedReminder[]>();
        for (const row of (data || [])) {
          const ticketId = row.ticket_id;
          if (!grouped.has(ticketId)) {
            grouped.set(ticketId, []);
          }
          grouped.get(ticketId)!.push(row as QueuedReminder);
        }
        setRemindersByTicket(grouped);
      } catch (error) {
        console.error('[useShopReminderCounts] Failed to fetch:', error);
      } finally {
        if (!isUnmountingRef.current) {
          setIsLoading(false);
        }
      }
    };

    fetchReminders();

    const channel = supabase
      .channel(`reminder-queue-shop-${shopId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'reminder_queue',
          filter: `shop_id=eq.${shopId}`,
        },
        () => {
          fetchReminders();
        }
      )
      .subscribe((status, err) => {
        // Batch 11: normalized log. Intentional CLOSED on shop change
        // / unmount → debug. CHANNEL_ERROR / TIMED_OUT / CLOSED-with-
        // err still surface as warn/error so a silent freeze of the
        // customer-facing badge counts is diagnosable.
        logRealtimeStatus('[useShopReminderCounts]', status, err);
      });

    return () => {
      isUnmountingRef.current = true;
      supabase.removeChannel(channel);
    };
  }, [shopId]);

  return { remindersByTicket, isLoading };
}
